/**
 * Optional-group skip detection.
 *
 * The wizard has two tiers of completeness:
 *   1. HARD-required fields — `validateStep()` blocks Next until they're filled.
 *   2. OPTIONAL groups — narratives, supplemental tables, and supportive
 *      sections that the RN may legitimately skip but should explicitly
 *      acknowledge skipping. (The DHS audit cares about completeness;
 *      a quietly-empty section is much riskier than an explicitly-skipped one.)
 *
 * For each step, this map declares which optional groups exist and how to
 * detect that the RN left them ENTIRELY empty (vs partially filled, which
 * is fine — an in-progress narrative is not a "skip"). On Next, the wizard
 * calls `findUnansweredOptionalGroups(step, data)` and if the result is
 * non-empty pops a confirm modal listing them so the RN can either go fill
 * them in or explicitly acknowledge the skip.
 */

const isStr = (v) => typeof v === "string" && v.trim().length > 0;
const arrFilled = (a) => Array.isArray(a) && a.some((row) => {
  if (!row || typeof row !== "object") return false;
  return Object.values(row).some((v) => (typeof v === "string" ? v.trim() : v));
});

/**
 * Per-step list of optional groups. Each entry is:
 *   { id, label, isFilled: (data) => boolean }
 * `isFilled` should return true if the group has ANY meaningful content.
 */
export const OPTIONAL_GROUPS_BY_STEP = {
  1: [
    {
      id: "client_emergency_contact",
      label: "Emergency contact info",
      isFilled: (d) => isStr(d?.client?.emergency_contact) || isStr(d?.client?.emergency_phone),
    },
    {
      id: "client_legal_rep",
      label: "Legal representative",
      isFilled: (d) => isStr(d?.client?.legal_rep),
    },
  ],
  3: [
    {
      id: "delegated_acts",
      label: "Delegated nursing acts",
      isFilled: (d) => arrFilled(d?.nursing?.delegated_acts) || isStr(d?.nursing?.delegation_summary),
    },
    {
      id: "skin_integrity",
      label: "Skin integrity narrative",
      isFilled: (d) => isStr(d?.nursing?.skin_integrity),
    },
  ],
  4: [
    {
      id: "communication_aids",
      label: "Communication aids / accommodations",
      isFilled: (d) => isStr(d?.communication?.aids_used) || isStr(d?.communication?.notes),
    },
  ],
  5: [
    {
      id: "surgical_history",
      label: "Surgical history narrative",
      isFilled: (d) => isStr(d?.medical_history?.surgical_history),
    },
    {
      id: "allergies",
      label: "Allergies",
      isFilled: (d) => isStr(d?.medical_history?.allergies) || arrFilled(d?.allergies),
    },
  ],
  6: [
    {
      id: "social_engagement",
      label: "Social engagement narrative",
      isFilled: (d) => isStr(d?.social?.engagement) || isStr(d?.social?.activities_summary),
    },
    {
      id: "caregiver_burden",
      label: "Caregiver burden / informal supports",
      isFilled: (d) => isStr(d?.social?.caregiver_burden) || isStr(d?.social?.informal_supports),
    },
    {
      id: "activity_schedule",
      label: "Client Activity Schedule",
      isFilled: (d) => arrFilled(d?.activity_schedule),
    },
  ],
  7: [
    {
      id: "narrative_summary",
      label: "RN narrative summary",
      isFilled: (d) => isStr(d?.nsa?.narrative_summary) || isStr(d?.nsa?.summary),
    },
    {
      id: "goals",
      label: "Care goals / outcomes",
      isFilled: (d) => arrFilled(d?.nsa?.goals) || arrFilled(d?.care_plan_goals),
    },
  ],
};

/**
 * Returns an array of {id, label} for the optional groups on `step` that
 * are entirely empty. The caller (Wizard.jsx Next button) shows a confirm
 * modal listing these so the RN can either go fill them or explicitly
 * acknowledge skipping.
 */
export function findUnansweredOptionalGroups(step, data) {
  const groups = OPTIONAL_GROUPS_BY_STEP[step] || [];
  return groups
    .filter((g) => !g.isFilled(data))
    .map(({ id, label }) => ({ id, label }));
}
